import { apiClient } from '../axiosInstance';
import { ENDPOINTS } from '../endpoints';
import type {
  Appointment,
  ServiceCategory,
  ServiceHistoryResponse,
} from '../../stores/useAppointmentsStore';
import type {
  AppointmentService,
  ServiceHistoryParams,
} from './interfaces/AppointmentService';
import {
  adaptServiceHistoryResponse,
  type AppointmentReportResponse,
} from './adapters/appointmentRow';
import { adaptAppointment, type AppointmentWire } from './adapters/appointment';
import {
  adaptCommentsByDateResponse,
  type AppointmentComment,
  type CommentsByDateResponse,
} from './adapters/comment';

export const appointmentService: AppointmentService = {
  async getAppointment(id) {
    // The /v1/appointments/:id wire shape diverges from the Vue 3 `Appointment`
    // interface — see adapters/appointment.ts for the full mapping. Without
    // this adapter every consumer that reads `scheduled_time` / `arrival_time`
    // / `departure_time` / `address.address1` / etc. would receive undefined
    // and the detail view would render blank.
    const { data } = await apiClient.get<AppointmentWire>(
      ENDPOINTS.v1.appointments.get(id),
    );
    return adaptAppointment(data);
  },

  async createAppointment(payload, promoCode) {
    const details = { ...payload.details };
    if (promoCode) {
      const existingNotes = details.notes ?? '';
      // <br> separators (not \n) — the tech app renders job notes as HTML,
      // so newlines collapse into a single run-on line. Matches legacy
      // consumer-portal's store.ts:230 promo-code prefix exactly.
      details.notes = `PROMO CODE: ${promoCode}<br>--------------------<br>${existingNotes}`;
    }
    const { data } = await apiClient.post<Appointment>(
      ENDPOINTS.v1.appointments.create,
      {
        appointment: { ...payload, details },
      },
    );
    return data;
  },

  async cancelAppointment(id, payload) {
    await apiClient.put(ENDPOINTS.v2.appointments.cancel(id), payload);
  },

  async rescheduleAppointment(id, payload) {
    await apiClient.put(ENDPOINTS.v2.appointments.update(id), payload);
    const { data } = await apiClient.get<AppointmentWire>(
      ENDPOINTS.v1.appointments.get(id),
    );
    return adaptAppointment(data);
  },

  async generateMagicLink(id) {
    const { data } = await apiClient.get<{
      token: string;
      expires_at: string;
    }>(ENDPOINTS.v1.appointments.shareGenerate(id));
    return data;
  },

  async fetchServiceCategories() {
    const { data } = await apiClient.get<ServiceCategory[]>(
      ENDPOINTS.v1.serviceCategories,
    );
    return data;
  },

  async fetchServiceHistory(
    params: ServiceHistoryParams,
  ): Promise<ServiceHistoryResponse> {
    // Wire shape is the flat Consumer::AppointmentsDatatable row — adapt it to
    // the nested AppointmentRow the UI consumes. See adapters/appointmentRow.ts.
    // `signal` is forwarded to axios so callers (AppointmentsView's debounced
    // search) can cancel in-flight requests via AbortController. Axios ignores
    // an undefined `signal` field so existing callers stay unaffected.
    const { data } = await apiClient.get<AppointmentReportResponse>(
      ENDPOINTS.reports.consumerAppointments,
      {
        params: {
          page: params.page + 1,
          per_page: params.rows,
          sort_by: params.sortField,
          sort_desc:
            params.sortOrder === undefined
              ? undefined
              : params.sortOrder === -1,
          query: params.filters?.global?.value,
          // PODZ-1694: comma-separated DB enum filter; undefined is dropped by
          // axios so nav-badge callers are unaffected.
          status: params.statusFilter,
        },
        signal: params.signal,
      },
    );
    return adaptServiceHistoryResponse(data);
  },

  async fetchAppointmentComments(jobId): Promise<AppointmentComment[]> {
    // v1/comments_controller#by_date returns:
    //   { pinned?: [...], "<display-date>": [...], ... }
    // The display-date keys are localized for screen reading and are
    // discarded by the adapter — each comment carries `created_at` in
    // ISO 8601 so client-side grouping (if needed) reads off that.
    //
    // 260601-jtr: scope comments by the Job, matching the tech app and the
    // admin portal. Those clients fetch notes with commentable_type=Job +
    // the job id (reference job id 8738631), so the customer portal now
    // requests the same scope to surface the same comment set rather than
    // the previously User-scoped set.
    const { data } = await apiClient.get<CommentsByDateResponse>(
      ENDPOINTS.v1.comments.byDate,
      {
        params: {
          'comment[commentable_type]': 'Job',
          'comment[commentable_id]': jobId,
        },
      },
    );
    return adaptCommentsByDateResponse(data);
  },
};
